import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconRefresh } from '@tabler/icons-react';
import { technicianInitials } from '../board/utils/technicianInitials';
import { BoardChatLayoutSwitcher } from '../board-chat/components/BoardChatLayoutSwitcher';
import {
  fetchCompletedRoute,
  fetchMonitoramentoTechnician,
  fetchTechnicianPanelDay,
  fetchTechnicianPanelProfilePhotoUrl,
  fetchTechnicianGoInstallStatus,
  fetchTodayRoute,
  removeOrderFromRouteByOs,
  updateOrderSequence,
} from './services/technicianPanelApi';
import { TECHNICIAN_PANEL_MOCK_HTML } from './mock/technicianPanelMockHtml';
import { applyTechnicianPanelDayToMockDom, clearTechnicianPanelDayMockDom } from './utils/technicianPanelMockDayRender';
import {
  applyTechnicianPanelOpenToMockDom,
  suggestNextSequence,
} from './utils/technicianPanelMockOpenRender';
import { applyTechnicianPanelCheckoutToMockDom } from './utils/technicianPanelMockCheckoutRender';
import { bindTechnicianPanelMockMap, formatTechnicianLiveStatus, refreshTechnicianPanelMockMap } from './utils/technicianPanelMockMapRender';
import { printTechnicianOpenRoute } from './utils/technicianPanelMockPrintRoute';
import { bindTechnicianPanelDomTips } from './utils/technicianPanelDomTip';
import useCutoverActive from '../pg-native/useCutoverActive';
import {
  TECHNICIAN_PANEL_ROUTE,
  clearRememberedTechnicianPanelTechnician,
  getTechnicianPanelLayout,
  rememberTechnicianPanelTechnician,
  setTechnicianPanelLayout,
  technicianPanelLayoutClass,
} from './utils/technicianPanelLayout';
import TechnicianPanelMockOsDrawer from './components/TechnicianPanelMockOsDrawer';
import TechnicianDiarioOryaPanel from './components/TechnicianDiarioOryaPanel';
import TechnicianTrackDateTimePopover from './components/TechnicianTrackDateTimePopover';
import { getCalendarBounds, toYmd } from '../equipment-consult/components/EquipmentConsultProductionPeriodPicker';
import { GoHoverTooltip } from '../go-config/components/GoHoverTooltip';
import '../go-config/components/GoHoverTooltip.css';
import './TechnicianPanelMockModal.css';

function applyAvatarToEl(avatarEl, { initials, photoUrl }) {
  if (!avatarEl) return;
  avatarEl.replaceChildren();
  if (photoUrl) {
    const img = document.createElement('img');
    img.className = 'tpm-avatar-img';
    img.src = photoUrl;
    img.alt = '';
    img.referrerPolicy = 'no-referrer';
    img.decoding = 'async';
    img.onerror = () => {
      avatarEl.replaceChildren();
      avatarEl.textContent = initials || '—';
    };
    avatarEl.appendChild(img);
    return;
  }
  avatarEl.textContent = initials || '—';
}

/**
 * Mock estático no mesmo DOM (selecionável), com bindings reais:
 * nome, status, fechadas/abertas, board/gantt (query do dia),
 * Em aberto (today-route) + roteirização (update-sequence).
 */
const TPM_TABS = new Set(['prod', 'open', 'co', 'map']);
const TPM_MAP_MODES = new Set(['live', 'plan', 'track']);

export default function TechnicianPanelMockModal({
  isOpen,
  onClose,
  technician,
  technicianId,
  openCount = 0,
  closedCount = 0,
  /** id_produto Go do cliente (3=Basic, 4=Premium) — vindo de /api/auth/verify */
  goIdProduto = null,
  /** Aba inicial ao abrir (ex.: 'map' via menu Roteirizar Premium) */
  initialTab = null,
  /** Modo do LiveMap ao abrir (ex.: 'plan' = Roteirizar) */
  initialMapMode = null,
  /** Nome do usuário logado (chat Orya Diário) */
  chatUserName = '',
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const cutoverActive = useCutoverActive();
  const rootRef = useRef(null);
  const [layout, setLayout] = useState(() => getTechnicianPanelLayout());
  const [layoutMountEl, setLayoutMountEl] = useState(null);
  const [monStatus, setMonStatus] = useState(null);
  const [workday, setWorkday] = useState(null);
  const [lunch, setLunch] = useState(null);
  const [dayData, setDayData] = useState(null);
  const [openOrders, setOpenOrders] = useState([]);
  const [openLoading, setOpenLoading] = useState(false);
  const [checkoutOrders, setCheckoutOrders] = useState([]);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [filterKey, setFilterKey] = useState(null);
  const [selectedOs, setSelectedOs] = useState(null);
  const [histOpen, setHistOpen] = useState(false);
  const [avatarPhotoUrl, setAvatarPhotoUrl] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('prod');
  const [editingOs, setEditingOs] = useState(null);
  const [savingSequence, setSavingSequence] = useState(false);
  const [trackDatePicker, setTrackDatePicker] = useState(null);
  const [prodDateYmd, setProdDateYmd] = useState(() => toYmd(new Date()));
  const [prodDatePicker, setProdDatePicker] = useState(null);
  const [diarioOrya, setDiarioOrya] = useState(null);
  const sequenceDraftRef = useRef('');
  const pendingMapModeRef = useRef(null);
  const closeTrackDatePicker = useCallback(() => setTrackDatePicker(null), []);
  const closeProdDatePicker = useCallback(() => setProdDatePicker(null), []);
  const closeDiarioOrya = useCallback(() => setDiarioOrya(null), []);

  const handleClose = useCallback(() => {
    clearRememberedTechnicianPanelTechnician();
    setDiarioOrya(null);
    onClose?.();
    if (location.pathname === TECHNICIAN_PANEL_ROUTE) {
      navigate('/app');
    }
  }, [onClose, location.pathname, navigate]);

  const handleLayoutChange = useCallback((next) => {
    setLayout(setTechnicianPanelLayout(next));
  }, []);

  const openCountLive = openOrders.length > 0 || !openLoading ? openOrders.length : openCount;
  const checkoutCountLive = checkoutOrders.length;

  // Deep-link: /app/roteiro enquanto o painel estiver aberto.
  useEffect(() => {
    if (!isOpen || !technician) return;
    rememberTechnicianPanelTechnician(technician);
    if (location.pathname !== TECHNICIAN_PANEL_ROUTE) {
      navigate(TECHNICIAN_PANEL_ROUTE);
    }
  }, [isOpen, technician, location.pathname, navigate]);

  const loadPanelData = useCallback(
    async ({ showVeil = false } = {}) => {
      if (!technicianId) return;
      if (showVeil) setRefreshing(true);
      setOpenLoading(true);
      setCheckoutLoading(true);
      try {
        const [mon, day, photoUrl, route, completed] = await Promise.all([
          fetchMonitoramentoTechnician(technicianId).catch(() => null),
          fetchTechnicianPanelDay(technicianId, prodDateYmd).catch(() => null),
          fetchTechnicianPanelProfilePhotoUrl(technicianId).catch(() => null),
          fetchTodayRoute(technicianId).catch(() => []),
          fetchCompletedRoute(technicianId).catch(() => []),
        ]);
        setMonStatus(mon?.technician?.status ?? null);
        setWorkday(mon?.workday ?? null);
        setLunch(mon?.lunch ?? null);
        setDayData(day);
        setAvatarPhotoUrl(photoUrl);
        setOpenOrders(Array.isArray(route) ? route : []);
        setCheckoutOrders(Array.isArray(completed) ? completed : []);
      } catch {
        setMonStatus(null);
        setWorkday(null);
        setLunch(null);
        setDayData(null);
        setAvatarPhotoUrl(null);
        setOpenOrders([]);
        setCheckoutOrders([]);
      } finally {
        setOpenLoading(false);
        setCheckoutLoading(false);
        if (showVeil) setRefreshing(false);
      }
    },
    [technicianId, prodDateYmd]
  );

  /** Refresh do contexto atual: aba principal ou submodo LiveMap. */
  const refreshCurrentView = useCallback(async () => {
    if (!technicianId) return;
    setRefreshing(true);
    setEditingOs(null);
    sequenceDraftRef.current = '';
    try {
      if (activeTab === 'map') {
        const mon = await fetchMonitoramentoTechnician(technicianId).catch(() => null);
        setMonStatus(mon?.technician?.status ?? null);
        setWorkday(mon?.workday ?? null);
        setLunch(mon?.lunch ?? null);
        const root = rootRef.current;
        if (root) {
          await refreshTechnicianPanelMockMap(root, mapOptsRef.current);
        }
        return;
      }

      if (activeTab === 'prod') {
        const [mon, day, photoUrl] = await Promise.all([
          fetchMonitoramentoTechnician(technicianId).catch(() => null),
          fetchTechnicianPanelDay(technicianId, prodDateYmd).catch(() => null),
          fetchTechnicianPanelProfilePhotoUrl(technicianId).catch(() => null),
        ]);
        setMonStatus(mon?.technician?.status ?? null);
        setWorkday(mon?.workday ?? null);
        setLunch(mon?.lunch ?? null);
        setDayData(day);
        setAvatarPhotoUrl(photoUrl);
        return;
      }

      if (activeTab === 'open') {
        setOpenLoading(true);
        try {
          const [mon, route] = await Promise.all([
            fetchMonitoramentoTechnician(technicianId).catch(() => null),
            fetchTodayRoute(technicianId).catch(() => []),
          ]);
          setMonStatus(mon?.technician?.status ?? null);
          setWorkday(mon?.workday ?? null);
          setLunch(mon?.lunch ?? null);
          setOpenOrders(Array.isArray(route) ? route : []);
        } finally {
          setOpenLoading(false);
        }
        return;
      }

      if (activeTab === 'co') {
        setCheckoutLoading(true);
        try {
          const [mon, completed] = await Promise.all([
            fetchMonitoramentoTechnician(technicianId).catch(() => null),
            fetchCompletedRoute(technicianId).catch(() => []),
          ]);
          setMonStatus(mon?.technician?.status ?? null);
          setWorkday(mon?.workday ?? null);
          setLunch(mon?.lunch ?? null);
          setCheckoutOrders(Array.isArray(completed) ? completed : []);
        } finally {
          setCheckoutLoading(false);
        }
      }
    } finally {
      setRefreshing(false);
    }
  }, [technicianId, activeTab, prodDateYmd]);

  const reloadOpenOrders = useCallback(async () => {
    if (!technicianId) return;
    try {
      const route = await fetchTodayRoute(technicianId);
      setOpenOrders(Array.isArray(route) ? route : []);
    } catch {
      /* mantém lista atual */
    }
  }, [technicianId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (refreshing || savingSequence) return;
      if (trackDatePicker) {
        setTrackDatePicker(null);
        return;
      }
      if (prodDatePicker) {
        setProdDatePicker(null);
        return;
      }
      if (editingOs) {
        setEditingOs(null);
        sequenceDraftRef.current = '';
        return;
      }
      if (histOpen) {
        setHistOpen(false);
        return;
      }
      if (selectedOs) {
        setSelectedOs(null);
        return;
      }
      handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, handleClose, selectedOs, histOpen, refreshing, editingOs, savingSequence, trackDatePicker, prodDatePicker]);

  useEffect(() => {
    if (!isOpen) {
      setMonStatus(null);
      setWorkday(null);
      setLunch(null);
      setDayData(null);
      setOpenOrders([]);
      setOpenLoading(false);
      setCheckoutOrders([]);
      setCheckoutLoading(false);
      setFilterKey(null);
      setSelectedOs(null);
      setHistOpen(false);
      setAvatarPhotoUrl(null);
      setRefreshing(false);
      setActiveTab('prod');
      setEditingOs(null);
      sequenceDraftRef.current = '';
      setSavingSequence(false);
      setTrackDatePicker(null);
      setProdDatePicker(null);
      setProdDateYmd(toYmd(new Date()));
    }
  }, [isOpen]);

  // Ao abrir / trocar técnico: Produtividade por padrão; honra destino explícito (ex. Roteirizar → LiveMap + plan).
  useEffect(() => {
    if (!isOpen || !technician) {
      pendingMapModeRef.current = null;
      return;
    }
    setActiveTab(TPM_TABS.has(initialTab) ? initialTab : 'prod');
    pendingMapModeRef.current = TPM_MAP_MODES.has(initialMapMode) ? initialMapMode : null;
    setFilterKey(null);
    setSelectedOs(null);
    setHistOpen(false);
    setEditingOs(null);
    sequenceDraftRef.current = '';
    setProdDateYmd(toYmd(new Date()));
    setProdDatePicker(null);
  }, [isOpen, technician, initialTab, initialMapMode]);

  useEffect(() => {
    if (!isOpen || !technicianId) return undefined;
    let cancelled = false;
    setOpenLoading(true);
    setCheckoutLoading(true);
    (async () => {
      try {
        const [mon, photoUrl, route, completed] = await Promise.all([
          fetchMonitoramentoTechnician(technicianId).catch(() => null),
          fetchTechnicianPanelProfilePhotoUrl(technicianId).catch(() => null),
          fetchTodayRoute(technicianId).catch(() => []),
          fetchCompletedRoute(technicianId).catch(() => []),
        ]);
        if (cancelled) return;
        setMonStatus(mon?.technician?.status ?? null);
        setWorkday(mon?.workday ?? null);
        setLunch(mon?.lunch ?? null);
        setAvatarPhotoUrl(photoUrl);
        setOpenOrders(Array.isArray(route) ? route : []);
        setCheckoutOrders(Array.isArray(completed) ? completed : []);
      } catch {
        if (!cancelled) {
          setMonStatus(null);
          setWorkday(null);
          setLunch(null);
          setAvatarPhotoUrl(null);
          setOpenOrders([]);
          setCheckoutOrders([]);
        }
      } finally {
        if (!cancelled) {
          setOpenLoading(false);
          setCheckoutLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, technicianId]);

  useEffect(() => {
    if (!isOpen || !technicianId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const day = await fetchTechnicianPanelDay(technicianId, prodDateYmd).catch(() => null);
        if (!cancelled) setDayData(day);
      } catch {
        if (!cancelled) setDayData(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, technicianId, prodDateYmd]);

  const html = useMemo(() => TECHNICIAN_PANEL_MOCK_HTML, []);

  // Hidrata o shell uma vez por abertura/técnico (evita React reaplicar o HTML mock).
  useLayoutEffect(() => {
    if (!isOpen || !rootRef.current) return undefined;
    rootRef.current.innerHTML = html;
    clearTechnicianPanelDayMockDom(rootRef.current);
    setLayoutMountEl(rootRef.current.querySelector('[data-tpm="layout-mount"]'));
    return () => {
      setLayoutMountEl(null);
      if (rootRef.current) rootRef.current.innerHTML = '';
    };
  }, [isOpen, technician, html]);

  // Badge "App Go · Atenção" ao lado do status: 1 chamada adiada por técnico, sem polling.
  useEffect(() => {
    if (!isOpen || !technicianId) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const info = await fetchTechnicianGoInstallStatus(technicianId).catch(() => null);
      if (cancelled || info?.status !== 'attention') return;
      const statusEl = rootRef.current?.querySelector('[data-tpm="status"]');
      if (!statusEl || statusEl.parentNode?.querySelector('.tpm-go-attention')) return;
      const badge = document.createElement('span');
      badge.className = 'tpm-status tpm-go-attention';
      const issues = Array.isArray(info.issues) ? info.issues.filter(Boolean) : [];
      badge.title = issues.length
        ? `Instalação do App Go com pontos a ajustar: ${issues.join(', ')}`
        : 'Instalação do App Go com pontos a ajustar';
      badge.setAttribute('aria-label', badge.title);
      const dot = document.createElement('span');
      dot.className = 'tpm-go-attention-dot';
      dot.setAttribute('aria-hidden', 'true');
      badge.appendChild(dot);
      badge.appendChild(document.createTextNode('App Go · Atenção'));
      statusEl.insertAdjacentElement('afterend', badge);
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isOpen, technicianId, technician]);

  // Contador Em aberto sem tocar no shell.
  useLayoutEffect(() => {
    if (!isOpen || !rootRef.current) return;
    const openTabCount = rootRef.current.querySelector('[data-tab="open"] .tpm-count');
    if (openTabCount) openTabCount.textContent = String(openCountLive);
  }, [isOpen, openCountLive]);

  const handleStartRoteirizar = useCallback(
    (osCode, currentSeq) => {
      sequenceDraftRef.current =
        currentSeq != null ? String(currentSeq) : String(suggestNextSequence(openOrders));
      setEditingOs(osCode);
    },
    [openOrders]
  );

  const handleSaveSequence = useCallback(
    async (osCode) => {
      if (!technicianId || !osCode || savingSequence) return;
      const newSequence = parseInt(sequenceDraftRef.current, 10);
      if (!Number.isFinite(newSequence) || newSequence < 1) {
        window.alert('Digite um número válido maior que 0.');
        return;
      }
      try {
        setSavingSequence(true);
        await updateOrderSequence({ technicianId, osCode, newSequence });
        setEditingOs(null);
        sequenceDraftRef.current = '';
        await reloadOpenOrders();
      } catch (err) {
        window.alert(err?.message || 'Erro ao definir ordem no roteiro.');
      } finally {
        setSavingSequence(false);
      }
    },
    [technicianId, savingSequence, reloadOpenOrders]
  );

  const handleRemoveRoteiro = useCallback(
    async (osCode) => {
      if (!osCode || savingSequence) return;
      const ok = window.confirm(`Remover a OS #${osCode} do roteiro?`);
      if (!ok) return;
      try {
        setSavingSequence(true);
        setEditingOs(null);
        sequenceDraftRef.current = '';
        await removeOrderFromRouteByOs({ orderNumber: osCode });
        await reloadOpenOrders();
      } catch (err) {
        window.alert(err?.message || 'Erro ao remover do roteiro.');
      } finally {
        setSavingSequence(false);
      }
    },
    [savingSequence, reloadOpenOrders]
  );

  const handlePrintOpen = useCallback(() => {
    printTechnicianOpenRoute({
      technicianName: technician || '—',
      orders: openOrders,
    });
  }, [technician, openOrders]);

  useEffect(() => {
    if (!isOpen || !rootRef.current) return undefined;
    const root = rootRef.current;
    const nameEl = root.querySelector('[data-tpm="tech-name"]');
    const statusEl = root.querySelector('[data-tpm="status"]');
    const avatarEl = root.querySelector('[data-tpm="avatar"]');
    const refreshEl = root.querySelector('[data-tpm="refresh"]');
    const kpis = root.querySelectorAll('[data-tpm="kpi"]');
    const initials = technicianInitials(technician);

    if (nameEl) nameEl.textContent = technician || '—';
    applyAvatarToEl(avatarEl, { initials, photoUrl: avatarPhotoUrl });
    if (statusEl) {
      statusEl.textContent = formatTechnicianLiveStatus(monStatus, dayData, workday, lunch);
      statusEl.classList.toggle('is-lunch', !!lunch?.active);
    }

    const fechadasValue = kpis[0]?.querySelector('.tpm-kpi-value');
    const abertasValue = kpis[1]?.querySelector('.tpm-kpi-value');
    if (fechadasValue) fechadasValue.textContent = String(closedCount ?? 0);
    if (abertasValue) abertasValue.textContent = String(openCountLive ?? 0);

    root.querySelectorAll('[data-tpm="tab"]').forEach((btn) => {
      const on = btn.getAttribute('data-tab') === activeTab;
      btn.classList.toggle('is-active', on);
    });
    root.querySelectorAll('[data-tpm-view]').forEach((view) => {
      const on = view.getAttribute('data-tpm-view') === activeTab;
      view.hidden = !on;
      view.classList.toggle('is-active', on);
    });

    const onTabClick = (e) => {
      const btn = e.currentTarget;
      const tab = btn.getAttribute('data-tab');
      if (!tab || tab === activeTab) return;
      setEditingOs(null);
      sequenceDraftRef.current = '';
      setActiveTab(tab);
    };
    root.querySelectorAll('[data-tpm="tab"]').forEach((btn) => {
      btn.addEventListener('click', onTabClick);
    });

    const PROD_PERIOD_ICON =
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/><path d="M8 14h.01"/><path d="M12 14h.01"/><path d="M16 14h.01"/><path d="M8 18h.01"/><path d="M12 18h.01"/><path d="M16 18h.01"/></svg>';
    const prodTab = root.querySelector('[data-tpm="tab"][data-tab="prod"]');
    let prodPeriod = prodTab?.querySelector('[data-tpm="prod-period"]') || null;
    if (prodTab && !prodPeriod) {
      prodPeriod = document.createElement('span');
      prodPeriod.className = 'tpm-prod-period';
      prodPeriod.setAttribute('data-tpm', 'prod-period');
      prodPeriod.innerHTML = `<span role="button" tabindex="0" class="tpm-prod-period-btn" data-tpm="prod-period-btn" aria-label="Dia da produtividade">${PROD_PERIOD_ICON}</span><span class="tpm-prod-period-tip" role="tooltip">Dia da produtividade</span>`;
      const ul = prodTab.querySelector('.tpm-ul');
      if (ul) prodTab.insertBefore(prodPeriod, ul);
      else prodTab.appendChild(prodPeriod);
    }
    // Se ficou fora da aba (versão antiga), move para dentro
    if (prodPeriod && prodTab && prodPeriod.parentElement !== prodTab) {
      const ul = prodTab.querySelector('.tpm-ul');
      if (ul) prodTab.insertBefore(prodPeriod, ul);
      else prodTab.appendChild(prodPeriod);
    }
    const prodPeriodBtn = prodPeriod?.querySelector('[data-tpm="prod-period-btn"]') || null;
    const onProdPeriod = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (activeTab !== 'prod' || refreshing) return;
      const rect = e.currentTarget.getBoundingClientRect();
      setProdDatePicker({
        anchorRect: {
          top: rect.top,
          bottom: rect.bottom,
          left: rect.left,
          right: rect.right,
          width: rect.width,
          height: rect.height,
        },
        currentDate: prodDateYmd || toYmd(new Date()),
      });
    };
    const onProdPeriodKey = (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      onProdPeriod(e);
    };
    if (prodPeriod) {
      prodPeriod.hidden = activeTab !== 'prod';
      prodPeriod.classList.toggle('is-visible', activeTab === 'prod');
      const today = toYmd(new Date());
      prodPeriod.classList.toggle('has-past-date', Boolean(prodDateYmd && prodDateYmd !== today));
      prodPeriodBtn?.addEventListener('click', onProdPeriod);
      prodPeriodBtn?.addEventListener('keydown', onProdPeriodKey);
    }

    const closeDefectPops = () => {
      root.querySelectorAll('[data-tpm="open-defect-pop"]').forEach((pop) => {
        pop.hidden = true;
      });
      root.querySelectorAll('[data-tpm="open-defect"]').forEach((btn) => {
        btn.classList.remove('is-open');
      });
    };

    const onDefectClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const os = e.currentTarget.getAttribute('data-os');
      const pop = root.querySelector(`[data-tpm="open-defect-pop"][data-os="${os}"]`);
      const btn = e.currentTarget;
      const willOpen = Boolean(pop?.hidden);
      closeDefectPops();
      if (willOpen && pop) {
        pop.hidden = false;
        btn.classList.add('is-open');
      }
    };
    const onDefectClose = (e) => {
      e.preventDefault();
      e.stopPropagation();
      closeDefectPops();
    };

    if (refreshEl) {
      refreshEl.classList.toggle('is-spinning', refreshing);
      refreshEl.disabled = refreshing || savingSequence;
      refreshEl.setAttribute('aria-busy', refreshing ? 'true' : 'false');
    }

    const onRefresh = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (refreshing || savingSequence) return;
      refreshCurrentView();
    };
    refreshEl?.addEventListener('click', onRefresh);

    const printEl = root.querySelector('[data-tpm="open-print"]');
    const onPrint = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (refreshing || savingSequence) return;
      handlePrintOpen();
    };
    printEl?.addEventListener('click', onPrint);

    if (dayData) {
      applyTechnicianPanelDayToMockDom(root, dayData, {
        filterKey,
        onFilterChange: setFilterKey,
        openCount: openCountLive,
        selectedOs: selectedOs?.os ?? null,
        onOsClick: setSelectedOs,
        dateYmd: prodDateYmd,
      });
    } else {
      clearTechnicianPanelDayMockDom(root);
    }

    const openTabCount = root.querySelector('[data-tab="open"] .tpm-count');
    if (openTabCount) openTabCount.textContent = String(openCountLive);

    if (activeTab === 'open') {
      applyTechnicianPanelOpenToMockDom(root, openOrders, {
        editingOs,
        sequenceValue: sequenceDraftRef.current,
        isSaving: savingSequence,
        onStartRoteirizar: handleStartRoteirizar,
        onRemoveRoteiro: handleRemoveRoteiro,
        onSequenceChange: (v) => {
          sequenceDraftRef.current = v;
        },
        onSaveSequence: handleSaveSequence,
        onCancelSequence: () => {
          setEditingOs(null);
          sequenceDraftRef.current = '';
        },
      });
      root.querySelectorAll('[data-tpm="open-defect"]').forEach((btn) => {
        btn.addEventListener('click', onDefectClick);
      });
      root.querySelectorAll('[data-tpm="open-defect-close"]').forEach((btn) => {
        btn.addEventListener('click', onDefectClose);
      });
      if (openLoading && openOrders.length === 0) {
        const label = root.querySelector('[data-tpm="open-count-label"]');
        if (label) label.textContent = 'Carregando…';
      }
    }

    const coTabCount = root.querySelector('[data-tab="co"] .tpm-count');
    if (coTabCount) coTabCount.textContent = String(checkoutCountLive);

    if (activeTab === 'co') {
      applyTechnicianPanelCheckoutToMockDom(root, checkoutOrders, {
        loading: checkoutLoading,
      });
    }

    // Tooltips modernos (data-tpm-tip) em toda a mock shell + LiveMap 3 modos.
    if (typeof root.__tpmTipCleanup === 'function') {
      root.__tpmTipCleanup();
      root.__tpmTipCleanup = null;
    }
    root.__tpmTipCleanup = bindTechnicianPanelDomTips(root);

    return () => {
      refreshEl?.removeEventListener('click', onRefresh);
      printEl?.removeEventListener('click', onPrint);
      prodPeriodBtn?.removeEventListener('click', onProdPeriod);
      prodPeriodBtn?.removeEventListener('keydown', onProdPeriodKey);
      root.querySelectorAll('[data-tpm="tab"]').forEach((btn) => {
        btn.removeEventListener('click', onTabClick);
      });
      root.querySelectorAll('[data-tpm="open-defect"]').forEach((btn) => {
        btn.removeEventListener('click', onDefectClick);
      });
      root.querySelectorAll('[data-tpm="open-defect-close"]').forEach((btn) => {
        btn.removeEventListener('click', onDefectClose);
      });
      if (typeof root.__tpmTipCleanup === 'function') {
        root.__tpmTipCleanup();
        root.__tpmTipCleanup = null;
      }
    };
  }, [
    isOpen,
    technician,
    monStatus,
    workday,
    lunch,
    openCountLive,
    closedCount,
    dayData,
    openOrders,
    openLoading,
    checkoutOrders,
    checkoutLoading,
    checkoutCountLive,
    filterKey,
    selectedOs,
    avatarPhotoUrl,
    refreshing,
    activeTab,
    prodDateYmd,
    refreshCurrentView,
    editingOs,
    savingSequence,
    handleStartRoteirizar,
    handleSaveSequence,
    handleRemoveRoteiro,
    handlePrintOpen,
    technicianId,
    cutoverActive,
  ]);

  const mapOptsRef = useRef({
    technicianId: null,
    technicianName: '',
    avatarPhotoUrl: null,
    initials: '',
    openOrders: [],
    statusLabel: '',
    goIdProduto: null,
    openTrackDatePicker: null,
    openDiarioOrya: null,
  });
  mapOptsRef.current = {
    technicianId,
    technicianName: technician || '',
    avatarPhotoUrl,
    initials: technicianInitials(technician),
    openOrders,
    statusLabel: formatTechnicianLiveStatus(monStatus, dayData, workday, lunch),
    goIdProduto: goIdProduto != null ? Number(goIdProduto) : null,
    get initialMapMode() {
      return pendingMapModeRef.current;
    },
    onInitialMapModeApplied: () => {
      pendingMapModeRef.current = null;
    },
    openTrackDatePicker: (payload) => {
      if (!payload?.anchorRect) return;
      setTrackDatePicker(payload);
    },
    openDiarioOrya: (payload) => {
      if (!payload?.dateYmd) return;
      setDiarioOrya({
        dateYmd: payload.dateYmd,
        timeFrom: payload.timeFrom || '',
        timeTo: payload.timeTo || '',
      });
    },
  };

  // LiveMap isolado: não desmonta o poll quando dayData/openOrders atualizam.
  useEffect(() => {
    if (!isOpen || activeTab !== 'map' || !rootRef.current) return undefined;
    const root = rootRef.current;
    const optsProxy = new Proxy(
      {},
      {
        get(_target, prop) {
          return mapOptsRef.current[prop];
        },
      }
    );
    const unbindMap = bindTechnicianPanelMockMap(root, optsProxy);
    return () => {
      unbindMap?.();
    };
  }, [isOpen, activeTab, technicianId, goIdProduto]);

  if (!isOpen) return null;

  const layoutClass = technicianPanelLayoutClass(layout);

  return createPortal(
    <div
      className={`tp-mock-overlay ${layoutClass}`}
      role="dialog"
      aria-modal="true"
      aria-label={technician ? `Novo painel — ${technician}` : 'Novo painel'}
      onClick={(e) => {
        if (refreshing || savingSequence) return;
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        className={`tp-mock-shell ${layoutClass}${selectedOs ? ' has-os-drawer' : ''}${histOpen ? ' has-hist-panel' : ''}${
          diarioOrya ? ' has-diario-orya' : ''
        }${refreshing || savingSequence ? ' is-refreshing' : ''}`}
      >
        <GoHoverTooltip text="Fechar" align="center" className="go-hover-tip-wrap--contents">
          <button
            type="button"
            className="tp-mock-close"
            aria-label="Fechar"
            onClick={handleClose}
            disabled={refreshing || savingSequence}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4" fill="none">
              <path d="M5 5l14 14M19 5L5 19" />
            </svg>
          </button>
        </GoHoverTooltip>
        <div
          ref={rootRef}
          className="tp-mock-content"
          data-tpm-host="true"
        />
        {layoutMountEl
          ? createPortal(
              <BoardChatLayoutSwitcher
                layout={layout}
                onChange={handleLayoutChange}
                tipProps={(text) => ({ 'data-tpm-tip': text })}
              />,
              layoutMountEl
            )
          : null}
        <TechnicianPanelMockOsDrawer
          os={selectedOs}
          onClose={() => {
            setHistOpen(false);
            setSelectedOs(null);
          }}
          onHistChange={setHistOpen}
        />
        <TechnicianDiarioOryaPanel
          open={!!diarioOrya}
          onClose={closeDiarioOrya}
          technicianId={technicianId}
          technicianName={technician || ''}
          chatUserName={chatUserName || ''}
          dateYmd={diarioOrya?.dateYmd || ''}
          timeFrom={diarioOrya?.timeFrom || ''}
          timeTo={diarioOrya?.timeTo || ''}
        />
        {refreshing || savingSequence ? (
          <div className="tpm-refresh-veil" role="status" aria-live="polite" aria-busy="true">
            <div className="tpm-refresh-card">
              <span className="tpm-refresh-spin" aria-hidden="true">
                <IconRefresh size={18} stroke={2} />
              </span>
              <span className="tpm-refresh-label">
                {savingSequence
                  ? 'Atualizando ordem…'
                  : activeTab === 'map'
                    ? 'Atualizando mapa…'
                    : activeTab === 'open'
                      ? 'Atualizando em aberto…'
                      : activeTab === 'co'
                        ? 'Atualizando check-out…'
                        : 'Atualizando painel…'}
              </span>
            </div>
          </div>
        ) : null}
      </div>
      {trackDatePicker ? (
        <TechnicianTrackDateTimePopover
          key={`${trackDatePicker.currentDate || 'track-date'}-${trackDatePicker.timeFrom || ''}-${trackDatePicker.timeTo || ''}`}
          anchorRect={trackDatePicker.anchorRect}
          currentDate={trackDatePicker.currentDate || null}
          timeFrom={trackDatePicker.timeFrom || ''}
          timeTo={trackDatePicker.timeTo || ''}
          technicianId={technicianId}
          minDate={getCalendarBounds().minDate}
          maxDate={getCalendarBounds().maxDate}
          zIndex={14050}
          onApply={(payload) => {
            trackDatePicker.onApply?.(payload);
          }}
          onClear={() => {
            trackDatePicker.onClear?.();
          }}
          onClose={closeTrackDatePicker}
        />
      ) : null}
      {prodDatePicker ? (
        <TechnicianTrackDateTimePopover
          key={`prod-date-${prodDatePicker.currentDate || 'today'}`}
          anchorRect={prodDatePicker.anchorRect}
          currentDate={prodDatePicker.currentDate || null}
          hideTime
          technicianId={technicianId}
          minDate={getCalendarBounds().minDate}
          maxDate={getCalendarBounds().maxDate}
          zIndex={14050}
          onApply={(payload) => {
            const next = payload?.date && /^\d{4}-\d{2}-\d{2}$/.test(payload.date)
              ? payload.date
              : toYmd(new Date());
            setProdDateYmd(next);
            setFilterKey(null);
          }}
          onClear={() => {
            setProdDateYmd(toYmd(new Date()));
            setFilterKey(null);
          }}
          onClose={closeProdDatePicker}
        />
      ) : null}
    </div>,
    document.body
  );
}
